'use client';

import { useEffect } from 'react';
import { useForm, SubmitHandler, Controller, useWatch } from 'react-hook-form';
import { Button, TextAreaInput } from '@aqarmap/aqarmap-ui';
import Modal from '~/components/Modal';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { useAddNoteMutation } from '~/redux/api/userApiSlice';
import { closeModal } from '~/redux/features/uiState/uiStateSlice';

type InputEmail = {
  note: string;
};

const NoteModal = ({
  listingId,
  setNote,
  note,
}: {
  listingId: number;
  setNote: (value: string) => void;
  note: string;
}) => {
  const NOTE_MAX_LENGTH = 150;
  const { t } = useTranslation('app');
  const { control, handleSubmit, setValue } = useForm<InputEmail>({
    defaultValues: {
      note: note,
    },
  });
  const noteValue = useWatch({ control, name: 'note' }) ?? '';
  const isModalOpen = useAppSelector((state) => state.uiState.modals.note);
  const [addNote, { isLoading }] = useAddNoteMutation();
  const dispatch = useAppDispatch();

  const onSubmit: SubmitHandler<InputEmail> = async (data) => {
    const res = await addNote({ listingId, note: data.note });
    if ('data' in res) {
      setNote(data.note);
      dispatch(closeModal('note'));
    }
  };

  const Footer = () => (
    <div className="w-full flex gap-2x">
      <Button
        ariaLabel="save-note"
        size="medium"
        variant="primary"
        className="flex-1"
        onClick={handleSubmit(onSubmit)}
        disabled={isLoading}
        isLoading={isLoading}
      >
        {note ? t('update') : t('save')}
      </Button>
      <Button
        ariaLabel="cancel-note"
        size="medium"
        variant="link"
        className="flex-1 bg-gray_light_1 no-underline hover:no-underline"
        onClick={() => {
          dispatch(closeModal('note'));
          setValue('note', note);
        }}
      >
        {t('cancel')}
      </Button>
    </div>
  );

  useEffect(() => {
    setValue('note', note);
  }, [note]);

  return (
    <Modal
      title={t('noteModalTitle')}
      modalKey="note"
      Footer={<Footer />}
      onClose={() => setValue('note', note)}
    >
      <div className="flex flex-col gap-y-1.5x w-full">
        <Controller
          control={control}
          name="note"
          rules={{ maxLength: NOTE_MAX_LENGTH }}
          render={({ field }) => (
            <TextAreaInput
              field={{
                placeholder: t('noteModalPlaceholder'),
                autoFocus: isModalOpen,
                className: 'h-[100px] !w-full [&>8]:!border-0',
                maxLength: NOTE_MAX_LENGTH,
                ...field,
                onChange: (e) => {
                  const value = e.target.value.slice(0, NOTE_MAX_LENGTH);
                  field.onChange(value);
                },
              }}
            />
          )}
        />
        <span className="text-caption-1 text-gray__dark_2">
          {noteValue.length}/{NOTE_MAX_LENGTH} {t('character')}
        </span>
      </div>
    </Modal>
  );
};

export default NoteModal;
